import {
  type ChatStatusData,
  getBranchListStatus,
  getChatListStatus,
  type ListStatus,
} from "../components/BuildStatusState";

type AttentionStatus = Exclude<ListStatus, null>;

export type AttentionBranchInput = {
  id: string;
  workflowId: string;
  headCommitChat: ChatStatusData | null | undefined;
  lastChatEntryAt: number | null;
  workflow?:
    | {
        id: string;
        name: string;
        spaceId: string;
        deletedAt: number | null;
        badgeSeed: string | null;
        pausedAt: number | null;
      }
    | undefined;
};

export type AttentionChatInput = {
  id: string;
  name: string;
  lastActiveAt: number;
  chat?:
    | {
        respondingAt: number | null;
        awaitingAction: boolean | null;
        lastChatEntryType: string | null;
      }
    | undefined;
};

export type AttentionItem = {
  key: string;
  name: string;
  status: AttentionStatus;
  since: number;
} & (
  | {
      kind: "branch";
      spaceId: string;
      workflowId: string;
      branchId: string;
      badgeSeed: string | null;
      pausedAt: number | null;
    }
  | { kind: "personalChat"; personalChatId: string }
);

const statusRank: Record<AttentionStatus, number> = {
  waiting: 0,
  ready: 1,
  building: 2,
  thinking: 2,
};

export function buildAttentionQueue({
  branches,
  lastSeenByWorkflowId,
  chatTabs,
  currentBranchId,
  currentWorkflowId,
  currentChatTabId,
}: {
  branches: readonly AttentionBranchInput[];
  lastSeenByWorkflowId: ReadonlyMap<string, number>;
  chatTabs: readonly AttentionChatInput[];
  currentBranchId?: string | undefined;
  currentWorkflowId?: string | undefined;
  currentChatTabId?: string | undefined;
}): AttentionItem[] {
  const items: AttentionItem[] = [];

  for (const branch of branches) {
    if (!branch.workflow || branch.workflow.deletedAt != null) continue;
    if (branch.id === currentBranchId) continue;
    // The workflow URL carries branchId only as an optional query param, so on
    // the default branch currentBranchId is undefined; fall back to the workflow
    // id so the branch you're viewing still isn't listed.
    if (!currentBranchId && branch.workflowId === currentWorkflowId) continue;
    const status = getBranchListStatus(
      branch,
      lastSeenByWorkflowId.get(branch.workflowId)
    );
    if (!status) continue;
    const since =
      status === "waiting" || status === "ready"
        ? (branch.lastChatEntryAt ?? 0)
        : (branch.headCommitChat?.respondingAt ?? branch.lastChatEntryAt ?? 0);
    items.push({
      kind: "branch",
      key: `branch:${branch.id}`,
      name: branch.workflow.name,
      status,
      since,
      spaceId: branch.workflow.spaceId,
      workflowId: branch.workflowId,
      branchId: branch.id,
      badgeSeed: branch.workflow.badgeSeed,
      pausedAt: branch.workflow.pausedAt,
    });
  }

  for (const tab of chatTabs) {
    if (!tab.chat) continue;
    if (tab.id === currentChatTabId) continue;
    // Personal chats keep lastChatEntryType at "UserMessage" for the whole
    // turn (only branch chats denormalize per-entry types), so awaitingAction
    // alone is the waiting signal.
    const status = tab.chat.awaitingAction
      ? "waiting"
      : getChatListStatus(tab.chat);
    // Chats have no per-entry seen tracking, so a finished chat can't be
    // distinguished from one whose result was already read — skip "ready".
    if (!status || status === "ready") continue;
    const since =
      status === "waiting"
        ? tab.lastActiveAt
        : (tab.chat.respondingAt ?? tab.lastActiveAt);
    items.push({
      kind: "personalChat",
      key: `personalChat:${tab.id}`,
      name: tab.name,
      status,
      since,
      personalChatId: tab.id,
    });
  }

  items.sort(
    (a, b) =>
      statusRank[a.status] - statusRank[b.status] ||
      // Most-recent-first, so the freshest activity leads, matching navBranches.
      b.since - a.since ||
      a.key.localeCompare(b.key)
  );

  return items;
}
